/**
 * TemplateFieldSpecs — declarative schema-driven field lists per kind.
 *
 * The full ChromeSpec / PaperSpec / WritingStyleSpec on the BE expose
 * deeply nested arrays (text_elements, decorations, slide_layout_overrides,
 * …). For the v2 editor MVP we surface the **top-level knobs** every
 * built-in template defines (palette anchors, font family, layout style,
 * paper format, voice tone). Power users will still need the v1 raw-JSON
 * fallback when they want to tweak per-element coordinates — that mode is
 * exposed via the read-only spec inspector at the bottom of the editor.
 *
 * The shapes live here (not inline in ``SchemaFormEditor``) so unit tests
 * and storybook fixtures can import the same source of truth.
 */

import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import type { TemplateKind } from "@ando/core";
import type { FieldSpec } from "@ando/ui";

/**
 * Internal localized FieldSpec — labels / placeholders / helperText /
 * select option labels are ``MessageDescriptor`` so Lingui can extract
 * them. The public ``getTemplateFieldSpecs(i18n)`` helper resolves these
 * to plain strings at render time, returning a ``FieldSpec[]`` that the
 * existing ``<FieldRenderer>`` consumes unchanged.
 */
interface LocalizedFieldSpec {
  readonly name: string;
  readonly label: MessageDescriptor;
  readonly type: FieldSpec["type"];
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly helperText?: MessageDescriptor;
  readonly options?: ReadonlyArray<{
    readonly value: string;
    readonly label: MessageDescriptor;
  }>;
}

/**
 * Chrome — slide deck spec. Top-level knobs:
 *   - ``palette.primary`` / ``palette.secondary``  → hex colors (text input)
 *   - ``fonts.header`` / ``fonts.body``            → font family select
 *   - ``layout``                                   → grid | single | split
 *   - ``animations_enabled``                       → bool
 *
 * BE shape: spec is opaque JSON; we read/write a flat subset and merge it
 * over the existing spec on update so non-edited keys round-trip unchanged.
 */
const CHROME_FIELDS: ReadonlyArray<LocalizedFieldSpec> = [
  {
    name: "palette.primary",
    label: msg`Colore primario`,
    type: "string",
    required: true,
    placeholder: "#FF7A1A",
    helperText: msg`Hex del colore brand principale (header, CTA, accenti).`,
  },
  {
    name: "palette.secondary",
    label: msg`Colore secondario`,
    type: "string",
    placeholder: "#D97706",
    helperText: msg`Hex usato per decorazioni e bordi.`,
  },
  {
    name: "fonts.header",
    label: msg`Font · intestazioni`,
    type: "select",
    required: true,
    // Font family names are brand identifiers — kept verbatim across locales.
    options: [
      { value: "Inter", label: msg`Inter` },
      { value: "Manrope", label: msg`Manrope` },
      { value: "Source Serif Pro", label: msg`Source Serif Pro` },
      { value: "Playfair Display", label: msg`Playfair Display` },
    ],
  },
  {
    name: "fonts.body",
    label: msg`Font · corpo`,
    type: "select",
    required: true,
    options: [
      { value: "Inter", label: msg`Inter` },
      { value: "Manrope", label: msg`Manrope` },
      { value: "Source Serif Pro", label: msg`Source Serif Pro` },
      { value: "Merriweather", label: msg`Merriweather` },
    ],
  },
  {
    name: "layout",
    label: msg`Layout slide`,
    type: "select",
    required: true,
    options: [
      { value: "grid", label: msg`Griglia (multi-colonna)` },
      { value: "single", label: msg`Singola colonna` },
      { value: "split", label: msg`Diviso (50/50)` },
    ],
  },
  {
    name: "animations_enabled",
    label: msg`Animazioni alle transizioni`,
    type: "boolean",
    helperText: msg`Disabilita per ridurre dimensioni file PPTX.`,
  },
];

/**
 * Paper — document spec. Top-level knobs:
 *   - ``format``         → 16:9 | 4:3 | A4 portrait | A4 landscape
 *   - ``margin_inches``  → numeric (0.25 - 2.0)
 *   - ``page_numbers``   → bool
 *   - ``footer_text``    → free text
 */
const PAPER_FIELDS: ReadonlyArray<LocalizedFieldSpec> = [
  {
    name: "format",
    label: msg`Formato`,
    type: "select",
    required: true,
    options: [
      { value: "16:9", label: msg`Presentazione 16:9` },
      { value: "4:3", label: msg`Presentazione 4:3` },
      { value: "a4_portrait", label: msg`A4 verticale` },
      { value: "a4_landscape", label: msg`A4 orizzontale` },
    ],
  },
  {
    name: "margin_inches",
    label: msg`Margini`,
    type: "number",
    required: true,
    placeholder: "0.75",
    helperText: msg`Pollici · range 0.25 – 2.0.`,
  },
  {
    name: "page_numbers",
    label: msg`Numeri di pagina`,
    type: "boolean",
  },
  {
    name: "footer_text",
    label: msg`Testo del footer`,
    type: "string",
    placeholder: "Confidenziale · Acme S.r.l.",
    helperText: msg`Lasciare vuoto per nessun footer.`,
  },
];

/**
 * Writing style — voice spec. Top-level knobs:
 *   - ``tone``                  → formal | casual | persuasive | technical
 *   - ``language``              → it | en
 *   - ``max_sentence_length``   → numeric (8 - 40 words)
 *   - ``emoji_allowed``         → bool
 *   - ``signature``             → free text
 */
const WRITING_STYLE_FIELDS: ReadonlyArray<LocalizedFieldSpec> = [
  {
    name: "tone",
    label: msg`Tono`,
    type: "select",
    required: true,
    options: [
      { value: "formal", label: msg`Formale` },
      { value: "casual", label: msg`Casual` },
      { value: "persuasive", label: msg`Persuasivo` },
      { value: "technical", label: msg`Tecnico` },
    ],
  },
  {
    name: "language",
    label: msg`Lingua`,
    type: "select",
    required: true,
    options: [
      { value: "it", label: msg`Italiano` },
      { value: "en", label: msg`English` },
    ],
  },
  {
    name: "max_sentence_length",
    label: msg`Lunghezza massima frase`,
    type: "number",
    placeholder: "20",
    helperText: msg`Parole · range consigliato 12 – 28.`,
  },
  {
    name: "emoji_allowed",
    label: msg`Consenti emoji`,
    type: "boolean",
  },
  {
    name: "signature",
    label: msg`Firma`,
    type: "string",
    placeholder: "Andrea — Ando",
    helperText: msg`Frase di chiusura ricorrente (vuoto = nessuna firma).`,
  },
];

const LOCALIZED_FIELD_SPECS: Record<TemplateKind, ReadonlyArray<LocalizedFieldSpec>> = {
  chrome: CHROME_FIELDS,
  paper: PAPER_FIELDS,
  writing_style: WRITING_STYLE_FIELDS,
};

/** Resolve a single localized spec to a renderer-ready ``FieldSpec``. */
function resolveFieldSpec(spec: LocalizedFieldSpec, i18n: I18n): FieldSpec {
  return {
    name: spec.name,
    label: i18n._(spec.label),
    type: spec.type,
    required: spec.required,
    placeholder: spec.placeholder,
    helperText: spec.helperText ? i18n._(spec.helperText) : undefined,
    options: spec.options?.map((o) => ({
      value: o.value,
      label: i18n._(o.label),
    })),
  };
}

/**
 * Returns the renderer-ready field specs (labels + helper text + select
 * option labels translated via ``i18n._``) for the given ``TemplateKind``.
 * Call this from React components that have ``useLingui()`` available.
 */
export function getTemplateFieldSpecs(kind: TemplateKind, i18n: I18n): ReadonlyArray<FieldSpec> {
  return LOCALIZED_FIELD_SPECS[kind].map((spec) => resolveFieldSpec(spec, i18n));
}

/**
 * Legacy non-localized snapshot of the field specs — kept for consumers
 * that still need synchronous access without an ``I18n`` instance (e.g.
 * unit tests, storybook fixtures). New code should call
 * ``getTemplateFieldSpecs(kind, i18n)`` from inside a React component
 * with ``useLingui()`` so labels follow the active locale. Strings
 * here are the Italian source — Lingui still extracts them via the
 * ``msg`` macro above.
 */
function resolveStaticFallback(spec: LocalizedFieldSpec): FieldSpec {
  // ``MessageDescriptor.message`` holds the source ICU string for the
  // ``msg`` template literal — we expose it as the static label so the
  // legacy callers keep rendering the Italian copy until they migrate.
  const message = (spec.label as { message?: string }).message ?? spec.name;
  const helperMessage = spec.helperText
    ? ((spec.helperText as { message?: string }).message ?? "")
    : undefined;
  return {
    name: spec.name,
    label: message,
    type: spec.type,
    required: spec.required,
    placeholder: spec.placeholder,
    helperText: helperMessage,
    options: spec.options?.map((o) => ({
      value: o.value,
      label: (o.label as { message?: string }).message ?? o.value,
    })),
  };
}

export const TEMPLATE_FIELD_SPECS: Record<TemplateKind, ReadonlyArray<FieldSpec>> = {
  chrome: CHROME_FIELDS.map(resolveStaticFallback),
  paper: PAPER_FIELDS.map(resolveStaticFallback),
  writing_style: WRITING_STYLE_FIELDS.map(resolveStaticFallback),
};

/**
 * Default spec used as the inheritance baseline for ``createTemplate`` when
 * the user doesn't pick a starting template. Keeps the BE validator from
 * rejecting an empty ``{}`` payload — every required field is satisfied
 * with a sane Italian-business default.
 */
export const DEFAULT_TEMPLATE_SPECS: Record<TemplateKind, Record<string, unknown>> = {
  chrome: {
    palette: { primary: "#FF7A1A", secondary: "#D97706" },
    fonts: { header: "Inter", body: "Inter" },
    layout: "grid",
    animations_enabled: true,
  },
  paper: {
    format: "a4_portrait",
    margin_inches: 0.75,
    page_numbers: true,
    footer_text: "",
  },
  writing_style: {
    tone: "formal",
    language: "it",
    max_sentence_length: 20,
    emoji_allowed: false,
    signature: "",
  },
};

/**
 * Read a dot-path key out of a nested record. Returns ``undefined`` when
 * the path doesn't resolve. Kept tiny on purpose — lodash.get would be
 * overkill for the 1-2 segment paths the field specs use.
 */
export function readPath(obj: Record<string, unknown>, path: string): unknown {
  const segments = path.split(".");
  let cursor: unknown = obj;
  for (const seg of segments) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[seg];
  }
  return cursor;
}

/**
 * Write a dot-path key into a nested record (returns a NEW object so React
 * sees the reference change). Creates intermediate objects as needed.
 */
export function writePath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split(".");
  if (segments.length === 0) return obj;
  const next: Record<string, unknown> = { ...obj };
  let cursor: Record<string, unknown> = next;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const seg = segments[i] ?? "";
    const existing = cursor[seg];
    const child: Record<string, unknown> =
      existing != null && typeof existing === "object"
        ? { ...(existing as Record<string, unknown>) }
        : {};
    cursor[seg] = child;
    cursor = child;
  }
  const lastSeg = segments[segments.length - 1] ?? "";
  cursor[lastSeg] = value;
  return next;
}
